import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  IconButton,
  MenuItem,
  FormControl,
  Select,
  Snackbar,
  Alert,
  CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { formFieldStyles, labelStyles, dialogTitleStyles, dialogDescriptionStyles } from "../../styles/formStyles";
import { ICategoryMinDTO, IInventoryQuantityResponseDTO, IItemMinDTO, ITypeMinDTO } from "../../interfaces/Orders";
import { getInventoryItem} from "../../services/Orders";
import { getCategoriesByTypeId, getItemsByCategoryId, getTypesWithItemsAvailable, getTypesWithItemsAvailablePublic } from "../../services/Inventory";


interface AddItemDialogProps {
  open: boolean;
  onClose: () => void;
  onAdd: (item: IInventoryQuantityResponseDTO) => void;
  companyId?: number;
  publicOrderId?: boolean;
}

const AddItemDialog =({ open, onClose, onAdd, companyId, publicOrderId }: AddItemDialogProps)=> {
  const [formData, setFormData] = useState({type: 0,category: 0,item: 0,});
  const [alltypes,setAllTypes] = useState<Array<ITypeMinDTO>>([]);
  const [allCategories,setAllCategories] = useState<Array<ICategoryMinDTO>>([]);
  const [allItems,setAllItems] = useState<Array<IItemMinDTO>>([]);
  const [selectedTypeId, setSelectedTypeId] = useState<number | null>(null);
  const [selectedCategoryId, setSelectedCategoryId] = useState<number | null>(null);
  const [loading,setLoading] = useState(false); 
  const [errMsg, setErrMsg] = useState("");
  const [openError, setOpenError] = useState(false);


  useEffect(() => {
    const fetchAllTypes = async () => {
      if (!companyId) return;
      if (open) {
        try {
          const typesRes = publicOrderId ? await getTypesWithItemsAvailablePublic(companyId) : await getTypesWithItemsAvailable();
          setAllTypes(Array.isArray(typesRes) ? typesRes : []);
        } catch (error) {
          setAllTypes([]);
        }
      }
    };
    fetchAllTypes();
  }, [open]);

  useEffect(() => {
    const fetchCategories = async () => {
      if (selectedTypeId) {
        try {
          const categoriesRes = await getCategoriesByTypeId(selectedTypeId);
          setAllCategories(Array.isArray(categoriesRes) ? categoriesRes : []);
        } catch (error) {
          setAllCategories([]);
        }
      } else {
        setAllCategories([]);
      }
    };
    fetchCategories();
  }, [selectedTypeId]);
  
  useEffect(() => {
    const fetchItems = async () => {
      if (selectedCategoryId) {
        try {
          const itemsRes = await  getItemsByCategoryId(selectedCategoryId);
          setAllItems(Array.isArray(itemsRes) ? itemsRes : []);
        } catch (error) {
          setAllItems([]);
        }
      } else {
        setAllItems([]);
      }
    };
    fetchItems();
  }, [selectedCategoryId]);
  const handleAdd = async () => {
    if (!formData.type || !formData.category || !formData.item) return;
  
    try {
      setLoading(true);
      const response = await getInventoryItem(formData.type, formData.category, formData.item);
      console.log("API Response:", response);
      
      if (response) {
        onAdd(response);
        setFormData({ type: 0, category: 0, item: 0 });
        setSelectedTypeId(null);
        setSelectedCategoryId(null);
        onClose();
      } else {
        setErrMsg(response?.errorResponse?.response?.data?.details || "Failed to add item");
        setOpenError(true);
      }
    } catch (error) {
      console.error("Error fetching inventory item:", error);
      setErrMsg("Error fetching inventory item");
      setOpenError(true);
    }finally{
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({ type: 0, category: 0, item: 0 });
    setSelectedTypeId(null);
    setSelectedCategoryId(null);
    onClose();
  };
  const handleSnackbarClose = () => {
    setOpenError(false);
  };
  return (
    <>
    <Dialog
      open={open}
      onClose={handleClose}
      PaperProps={{
        sx: {
          borderRadius: "12px",
          width: "30%",
          maxWidth: "none",
          display: "flex",
          flexDirection: "column",
          maxHeight: "90vh",
        },
      }}
    >
      {/* Header */}
      <Box sx={{ px: 3, pt: 2, pb: 1, borderBottom: "1px solid", borderColor: "grey.100", display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
        <Box>
          <Typography sx={dialogTitleStyles}>
            Add Item
          </Typography>
          <Typography sx={dialogDescriptionStyles}>
            Add Item to the Order. After you done click on item order.
          </Typography>
        </Box>
        <IconButton onClick={handleClose} size="small" sx={{ mt: -0.5 }}>
          <CloseIcon />
        </IconButton>
      </Box>

      {/* Content */}
      <Box sx={{ flex: 1, overflowY: "auto", px: 3, py: 2, "&::-webkit-scrollbar": { width: "8px" }, "&::-webkit-scrollbar-track": { background: "transparent" }, "&::-webkit-scrollbar-thumb": { background: "#CBD5E1", borderRadius: "4px" } }}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <Box>
            <Typography component="label" sx={labelStyles}>
              Type <span style={{ color: "red" }}>*</span>
            </Typography>
            <FormControl fullWidth size="small" sx={{ mt: 0.5, mb: 2 }}>
              <Select
              size="small"
              value={formData.type}
              onChange={(e) =>{ 
                const id = Number(e.target.value);
                setSelectedTypeId(id);
                setSelectedCategoryId(null);
                setFormData({ ...formData, type: id })}}
              sx={formFieldStyles}
              MenuProps={{PaperProps: {style: {maxHeight: 150,},},}}
            >
              <MenuItem value={0} disabled>Select Type</MenuItem>
                {alltypes.map((type) => (
                  <MenuItem key={type.id} value={type.id}>
                    {type.typeName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          <Box>
            <Typography component="label" sx={labelStyles}>
              Category <span style={{ color: "red" }}>*</span>
            </Typography>
            <FormControl fullWidth size="small" sx={{ mt: 0.5, mb: 2 }}>
              <Select
              size="small"
              value={formData.category}
              onChange={(e) =>{
                const id = Number(e.target.value);
                setSelectedCategoryId(id);
                setFormData({ ...formData, category: id })
              }}
              sx={formFieldStyles}
              MenuProps={{PaperProps: {style: {maxHeight: 150,},},}}
            >
              <MenuItem value={0} disabled>Select Category</MenuItem>
                {allCategories.map((cat) => (
                  <MenuItem key={cat.id} value={cat.id}>
                    {cat.categoryName}
                  </MenuItem>
                ))}
            </Select>
            </FormControl>
          </Box>

          <Box>
            <Typography component="label" sx={labelStyles}>
              Item Name <span style={{ color: "red" }}>*</span>
            </Typography>
            <FormControl fullWidth size="small" sx={{ mt: 0.5, mb: 2 }}>
            <Select
              fullWidth
              size="small"
              value={formData.item}
              onChange={(e) => {
                const id = Number(e.target.value);
                setFormData({ ...formData, item: id })
              }}
              sx={formFieldStyles}
              MenuProps={{PaperProps: {style: {maxHeight: 150,},},}}
            >
              <MenuItem value={0} disabled>Select Item</MenuItem>
                {allItems.map((item) => (
                  <MenuItem key={item.id} value={item.id}>
                    {item.itemName}
                  </MenuItem>
                ))}
            </Select>
            </FormControl>
          </Box>
        </Box>
      </Box>

      {/* Footer */}      <Box sx={{ borderTop: "1px solid", borderColor: "grey.100", px: 3, py: 2, display: "flex", justifyContent: "space-between" }}>
        <Button
          variant="outlined"
          onClick={handleClose}
          disabled={loading}
          sx={{
            borderRadius: "6px",
            border: "1px solid",
            borderColor: "primary.main",
            boxShadow: "0 0 10.7px 0 rgba(0, 94, 255, 0.20) inset",
            textTransform: "none",
            color: "primary.main",
            px: 2,
            "&:hover": {
              border: "1px solid",
              borderColor: "primary.main",
            },
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleAdd}
          disabled={loading}
          sx={{
            borderRadius: "6px",
            border: "1px solid",
            borderColor: "primary.main",
            boxShadow: "0 0 10.7px 0 rgba(0, 0, 0, 0.25) inset",
            textTransform: "none",
            px: 3,
            "&:hover": {
              backgroundColor: "primary.main",
              boxShadow: "0 0 10.7px 0 rgba(0, 0, 0, 0.25) inset",
            },
          }}
        >
          {loading ? <CircularProgress size={24} color="inherit" /> : "Add"}
        </Button>
      </Box>
    </Dialog>
    <Snackbar anchorOrigin={{ vertical: "top", horizontal: "right" }} open={openError} autoHideDuration={5000} onClose={handleSnackbarClose}>
    <Alert severity="error" variant="filled" sx={{ width: "100%" }}>{errMsg}</Alert>
  </Snackbar>
  </>
  );
}
export default AddItemDialog;
